import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, User, Briefcase, ArrowRight, CheckCircle, Clock,
  MessageSquare, XCircle, Send, Save, Shield, Search, Plus,
  Edit3, Trash2, Download, ChevronDown, ChevronRight, X, Eye,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import usePageTitle from '../hooks/usePageTitle';
import apiClient from '../api/client';

const DEV_APPLICATIONS = [
  {
    id: 'app-001',
    full_name: 'Dev User',
    email: 'dev@ccai.local',
    position: 'AI/Full Stack Engineer',
    employment_type: 'full_time',
    status: 'under_review',
    linkedin_url: 'https://linkedin.com/in/devuser',
    created_at: '2026-02-20T10:30:00Z',
  },
  {
    id: 'app-002',
    full_name: 'Dev User',
    email: 'dev@ccai.local',
    position: 'Marketing',
    employment_type: 'intern',
    status: 'submitted',
    linkedin_url: '',
    created_at: '2026-02-22T14:15:00Z',
  },
  {
    id: 'app-003',
    full_name: 'Dev User',
    email: 'dev@ccai.local',
    position: 'Campus Ambassador / Area or City Lead',
    employment_type: 'volunteer',
    status: 'interview',
    linkedin_url: '',
    created_at: '2026-01-15T09:00:00Z',
  },
];

const DEV_ALL_APPLICATIONS = [
  {
    id: 'app-001',
    full_name: 'Alice Chen',
    email: 'alice@example.com',
    position: 'AI/Full Stack Engineer',
    employment_type: 'full_time',
    status: 'under_review',
    cover_letter: 'I am passionate about AI and have 5 years of full-stack development experience. I would love to contribute to the CCAI platform.',
    linkedin_url: 'https://linkedin.com/in/alicechen',
    phone: '555-0101',
    reviewer_notes: '',
    created_at: '2026-02-20T10:30:00Z',
  },
  {
    id: 'app-002',
    full_name: 'Bob Wang',
    email: 'bob@example.com',
    position: 'Marketing',
    employment_type: 'intern',
    status: 'submitted',
    cover_letter: '',
    linkedin_url: '',
    phone: '',
    reviewer_notes: '',
    created_at: '2026-02-22T14:15:00Z',
  },
  {
    id: 'app-003',
    full_name: 'Carol Zhang',
    email: 'carol@example.com',
    position: 'Campus Ambassador / Area or City Lead',
    employment_type: 'volunteer',
    status: 'interview',
    cover_letter: 'As a student leader at UCLA, I have organized many community events...',
    linkedin_url: 'https://linkedin.com/in/carolzhang',
    phone: '555-0303',
    reviewer_notes: 'Strong community involvement. Schedule interview for next week.',
    created_at: '2026-01-15T09:00:00Z',
  },
  {
    id: 'app-004',
    full_name: 'David Liu',
    email: 'david.liu@example.com',
    position: 'AI/Full Stack Engineer',
    employment_type: 'full_time',
    status: 'accepted',
    cover_letter: 'I bring 3 years of ML engineering and a strong open-source track record.',
    linkedin_url: 'https://linkedin.com/in/davidliu',
    phone: '555-0404',
    reviewer_notes: 'Excellent technical skills. Offer sent.',
    created_at: '2026-01-05T11:00:00Z',
  },
  {
    id: 'app-005',
    full_name: 'Emily Xu',
    email: 'emily@example.com',
    position: 'Marketing',
    employment_type: 'part_time',
    status: 'rejected',
    cover_letter: 'I have experience in social media marketing.',
    linkedin_url: '',
    phone: '555-0505',
    reviewer_notes: 'Position filled.',
    created_at: '2026-02-01T08:45:00Z',
  },
  {
    id: 'app-006',
    full_name: 'Frank Zhao',
    email: 'frank.z@example.com',
    position: 'Data Analyst',
    employment_type: 'intern',
    status: 'under_review',
    cover_letter: 'Currently pursuing MS in Data Science at Stanford.',
    linkedin_url: 'https://linkedin.com/in/frankzhao',
    phone: '555-0606',
    reviewer_notes: '',
    created_at: '2026-02-18T16:30:00Z',
  },
];

const DEV_USERS = [
  { id: 'u-001', username: 'dev_user', email: 'dev@ccai.local', role: 'admin', created_at: '2026-01-01T00:00:00Z' },
  { id: 'u-002', username: 'alice_chen', email: 'alice@example.com', role: 'employee', created_at: '2026-02-15T00:00:00Z' },
  { id: 'u-003', username: 'bob_wang', email: 'bob@example.com', role: 'employee', created_at: '2026-02-18T00:00:00Z' },
  { id: 'u-004', username: 'carol_zhang', email: 'carol@example.com', role: 'manager', created_at: '2026-01-20T00:00:00Z' },
  { id: 'u-005', username: 'david_liu', email: 'david.liu@example.com', role: 'employee', created_at: '2026-01-10T00:00:00Z' },
];

const STATUS_CONFIG = {
  submitted:    { icon: Send,         color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20' },
  under_review: { icon: Clock,        color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  interview:    { icon: MessageSquare, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  accepted:     { icon: CheckCircle,  color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/20' },
  rejected:     { icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/20' },
};

const PositionForm = ({ position, onSave, onCancel, t }) => {
  const [form, setForm] = useState({
    title: position.title || '',
    description: position.description || '',
    employment_type: position.employment_type || 'full_time',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave({ ...position, ...form });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 rounded-xl bg-dark-bg/50 border border-accent/20 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">{t('dashboard.admin.positionTitle')}</label>
        <input
          type="text"
          value={form.title}
          onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
          placeholder={t('dashboard.admin.positionTitlePlaceholder')}
          className="w-full px-3 py-2 rounded-lg bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">{t('dashboard.admin.positionDescription')}</label>
        <textarea
          value={form.description}
          onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t('dashboard.admin.positionDescriptionPlaceholder')}
          rows={2}
          className="w-full px-3 py-2 rounded-lg bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">{t('dashboard.admin.positionType')}</label>
        <select
          value={form.employment_type}
          onChange={e => setForm(prev => ({ ...prev, employment_type: e.target.value }))}
          className="px-3 py-2 rounded-lg bg-dark-card border border-dark-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        >
          {['full_time', 'part_time', 'intern', 'volunteer'].map(et => (
            <option key={et} value={et}>{t(`dashboard.admin.employmentTypes.${et}`) || et}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Save className="h-3.5 w-3.5" />
          {t('dashboard.admin.savePosition')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-dark-card border border-dark-border text-gray-400 text-sm hover:text-white transition-colors"
        >
          {t('dashboard.admin.cancelEdit')}
        </button>
      </div>
    </form>
  );
};

const Dashboard = () => {
  const { t } = useLanguage();
  const { user, isDevMode } = useAuth();
  usePageTitle(t('dashboard.title') || 'Dashboard');

  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);

  const [profileForm, setProfileForm] = useState({
    phone: '',
    bio: '',
    linkedin_url: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Admin state
  const isAdmin = user?.role === 'admin';
  const [adminApps, setAdminApps] = useState([]);
  const [adminFilter, setAdminFilter] = useState('all');
  const [adminSearch, setAdminSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminPositions, setAdminPositions] = useState([]);
  const [editingPosition, setEditingPosition] = useState(null);
  const [newPosition, setNewPosition] = useState(null);
  const [openSections, setOpenSections] = useState({ applications: true, positions: false, users: false });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadApps = async () => {
      setLoadingApps(true);
      try {
        if (isDevMode) {
          await new Promise(r => setTimeout(r, 500));
          setApplications(DEV_APPLICATIONS);
        } else if (supabase && user?.email) {
          const { data, error } = await supabase
            .from('applications')
            .select('*')
            .eq('email', user.email)
            .order('created_at', { ascending: false });
          if (error) throw error;
          setApplications(data || []);
        } else {
          const data = await apiClient.getMyApplications();
          setApplications(Array.isArray(data) ? data : data?.items || []);
        }
      } catch {
        setApplications([]);
      } finally {
        setLoadingApps(false);
      }
    };
    loadApps();
  }, [isDevMode, user?.email]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        phone: user.phone || '',
        bio: user.bio || '',
        linkedin_url: user.linkedin_url || '',
      });
    }
  }, [user]);

  // Load admin data
  useEffect(() => {
    if (!isAdmin) return;
    const loadAdminData = async () => {
      try {
        if (isDevMode) {
          await new Promise(r => setTimeout(r, 300));
          setAdminApps(DEV_ALL_APPLICATIONS);
          setAdminUsers(DEV_USERS);
          const defaultPositions = [
            { id: 'pos-1', title: 'AI/Full Stack Engineer', description: 'Build AI-powered features and full-stack web applications.', employment_type: 'full_time' },
            { id: 'pos-2', title: 'Marketing', description: 'Drive brand awareness and community engagement.', employment_type: 'intern' },
            { id: 'pos-3', title: 'Campus Ambassador / Area or City Lead', description: 'Represent CCAI on campus and lead local community initiatives.', employment_type: 'volunteer' },
            { id: 'pos-4', title: 'Data Analyst', description: 'Analyze community data to drive product decisions.', employment_type: 'intern' },
          ];
          setAdminPositions(defaultPositions);
        } else {
          const [appsRes, usersRes] = await Promise.all([
            apiClient.getAllApplications(),
            apiClient.getUsers(),
          ]);
          setAdminApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
          setAdminUsers(Array.isArray(usersRes) ? usersRes : []);
        }
      } catch {
        // fail silently
      }
    };
    loadAdminData();
  }, [isAdmin, isDevMode]);

  const toggleSection = useCallback((key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleStatusChange = async (appId, newStatus) => {
    if (isDevMode) {
      setAdminApps(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      if (selectedApp?.id === appId) setSelectedApp(prev => ({ ...prev, status: newStatus }));
    } else {
      try {
        await apiClient.updateApplicationStatus(appId, { status: newStatus });
        setAdminApps(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
        if (selectedApp?.id === appId) setSelectedApp(prev => ({ ...prev, status: newStatus }));
      } catch { /* silently fail */ }
    }
  };

  const handleNotesChange = (appId, notes) => {
    setAdminApps(prev => prev.map(a => a.id === appId ? { ...a, reviewer_notes: notes } : a));
    if (selectedApp?.id === appId) setSelectedApp(prev => ({ ...prev, reviewer_notes: notes }));
  };

  const handleSaveNotes = async (appId) => {
    const app = adminApps.find(a => a.id === appId);
    if (!app) return;
    if (!isDevMode) {
      try {
        await apiClient.updateApplicationStatus(appId, { reviewer_notes: app.reviewer_notes });
      } catch { /* silently fail */ }
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (isDevMode) {
      setAdminUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } else {
      try {
        await apiClient.updateUserRole(userId, newRole);
        setAdminUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      } catch { /* silently fail */ }
    }
  };

  const handleDeletePosition = (posId) => {
    if (!window.confirm(t('dashboard.admin.confirmDelete'))) return;
    setAdminPositions(prev => prev.filter(p => p.id !== posId));
  };

  const handleSavePosition = (pos) => {
    if (pos._isNew) {
      const { _isNew, ...rest } = pos;
      setAdminPositions(prev => [...prev, { ...rest, id: `pos-${Date.now()}` }]);
      setNewPosition(null);
    } else {
      setAdminPositions(prev => prev.map(p => p.id === pos.id ? pos : p));
      setEditingPosition(null);
    }
  };

  const filteredAdminApps = adminApps.filter(app => {
    if (adminFilter !== 'all' && app.status !== adminFilter) return false;
    if (adminSearch) {
      const q = adminSearch.toLowerCase();
      return (
        app.full_name.toLowerCase().includes(q) ||
        app.email.toLowerCase().includes(q) ||
        app.position.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileSaved(false);
    try {
      if (!isDevMode) {
        await apiClient.updateUserProfile(profileForm);
      } else {
        await new Promise(r => setTimeout(r, 600));
      }
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch {
      // silently fail for dev
    } finally {
      setProfileSaving(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  const tabs = [
    { key: 'applications', label: t('dashboard.tabs.applications'), icon: FileText },
    { key: 'profile',      label: t('dashboard.tabs.profile'),      icon: User },
    ...(isAdmin ? [{ key: 'admin', label: t('dashboard.tabs.admin'), icon: Shield }] : []),
  ];

  return (
    <div className="overflow-x-hidden">
      <section className="relative pt-28 pb-6 sm:pt-36 overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
          {/* Dev banner */}
          {isDevMode && (
            <div className="mb-4 px-4 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs font-medium text-center">
              {t('dashboard.devBanner')}
            </div>
          )}

          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">{t('dashboard.title')}</h1>

          {/* Tabs */}
          <div className="flex gap-1 bg-dark-card border border-dark-border rounded-xl p-1 mb-8">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    active
                      ? 'bg-accent/10 text-accent border border-accent/20'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div>
              {loadingApps ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-16">
                  <Briefcase className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{t('dashboard.noApplications')}</h3>
                  <p className="text-gray-400 text-sm mb-6">{t('dashboard.noApplicationsHint')}</p>
                  <Link
                    to="/careers"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent text-white font-semibold text-sm rounded-full hover:bg-accent-hover transition-colors"
                  >
                    {t('dashboard.browsePositions')}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map(app => {
                    const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.submitted;
                    const StatusIcon = cfg.icon;
                    const statusLabel = t(`dashboard.statusLabels.${app.status}`) || app.status;
                    return (
                      <div key={app.id} className="border border-dark-border bg-dark-card rounded-xl p-5 hover:border-dark-border-light transition-colors duration-200">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-white font-semibold text-base truncate">{app.position}</h3>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-gray-400">
                              <span>{t('dashboard.applicationCard.submitted')}: {formatDate(app.created_at)}</span>
                              {app.employment_type && (
                                <span className="text-gray-500">{t(`careers.formTypeOptions.${app.employment_type}`) || app.employment_type}</span>
                              )}
                            </div>
                          </div>
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${cfg.bg} ${cfg.border} ${cfg.color} border`}>
                            <StatusIcon className="h-3.5 w-3.5" />
                            {statusLabel}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center text-xl font-bold text-white uppercase">
                  {(user?.username || 'U')[0]}
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg">{user?.username}</h2>
                  <p className="text-gray-400 text-sm">{user?.email}</p>
                </div>
              </div>

              <form onSubmit={handleProfileSave} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('dashboard.profile.username')}</label>
                  <input
                    type="text"
                    value={user?.username || ''}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-gray-500 text-sm cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('dashboard.profile.email')}</label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-gray-500 text-sm cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('dashboard.profile.phone')}</label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={e => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder={t('dashboard.profile.phonePlaceholder')}
                    className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('dashboard.profile.bio')}</label>
                  <textarea
                    value={profileForm.bio}
                    onChange={e => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder={t('dashboard.profile.bioPlaceholder')}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('dashboard.profile.linkedin')}</label>
                  <input
                    type="url"
                    value={profileForm.linkedin_url}
                    onChange={e => setProfileForm(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder={t('dashboard.profile.linkedinPlaceholder')}
                    className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={profileSaving}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white font-semibold text-sm rounded-xl hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {profileSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('dashboard.profile.saving')}
                    </>
                  ) : profileSaved ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      {t('dashboard.profile.saved')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t('dashboard.profile.save')}
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === 'admin' && isAdmin && (
            <div className="space-y-4">

              {/* ── Section 1: Applications Review ── */}
              <div className="border border-dark-border bg-dark-card rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('applications')}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-accent" />
                    <h2 className="text-white font-semibold text-base">{t('dashboard.admin.applicationsTitle')}</h2>
                    <span className="text-xs text-gray-500">({adminApps.length})</span>
                  </div>
                  {openSections.applications ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                </button>

                {openSections.applications && (
                  <div className="px-5 pb-5 space-y-4">
                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <select
                        value={adminFilter}
                        onChange={e => setAdminFilter(e.target.value)}
                        className="px-3 py-2 rounded-lg bg-dark-bg border border-dark-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      >
                        <option value="all">{t('dashboard.admin.filterAll')}</option>
                        {Object.keys(STATUS_CONFIG).map(s => (
                          <option key={s} value={s}>{t(`dashboard.statusLabels.${s}`) || s}</option>
                        ))}
                      </select>
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <input
                          type="text"
                          value={adminSearch}
                          onChange={e => setAdminSearch(e.target.value)}
                          placeholder={t('dashboard.admin.searchPlaceholder')}
                          className="w-full pl-9 pr-4 py-2 rounded-lg bg-dark-bg border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent"
                        />
                      </div>
                    </div>

                    {/* Table */}
                    {filteredAdminApps.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-8">{t('dashboard.admin.noResults')}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-dark-border text-gray-400 text-left">
                              <th className="pb-3 pr-4 font-medium">{t('dashboard.admin.colName')}</th>
                              <th className="pb-3 pr-4 font-medium hidden sm:table-cell">{t('dashboard.admin.colEmail')}</th>
                              <th className="pb-3 pr-4 font-medium">{t('dashboard.admin.colPosition')}</th>
                              <th className="pb-3 pr-4 font-medium hidden md:table-cell">{t('dashboard.admin.colType')}</th>
                              <th className="pb-3 pr-4 font-medium hidden md:table-cell">{t('dashboard.admin.colDate')}</th>
                              <th className="pb-3 font-medium">{t('dashboard.admin.colStatus')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAdminApps.map(app => {
                              const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.submitted;
                              const StatusIcon = cfg.icon;
                              const isSelected = selectedApp?.id === app.id;
                              return (
                                <React.Fragment key={app.id}>
                                  <tr
                                    onClick={() => setSelectedApp(isSelected ? null : app)}
                                    className={`border-b border-dark-border/50 cursor-pointer transition-colors ${isSelected ? 'bg-accent/5' : 'hover:bg-white/5'}`}
                                  >
                                    <td className="py-3 pr-4 text-white font-medium">{app.full_name}</td>
                                    <td className="py-3 pr-4 text-gray-400 hidden sm:table-cell">{app.email}</td>
                                    <td className="py-3 pr-4 text-gray-300 max-w-[200px] truncate">{app.position}</td>
                                    <td className="py-3 pr-4 text-gray-400 hidden md:table-cell">{t(`dashboard.admin.employmentTypes.${app.employment_type}`) || app.employment_type}</td>
                                    <td className="py-3 pr-4 text-gray-400 hidden md:table-cell">{formatDate(app.created_at)}</td>
                                    <td className="py-3">
                                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${cfg.bg} ${cfg.border} ${cfg.color} border`}>
                                        <StatusIcon className="h-3 w-3" />
                                        {t(`dashboard.statusLabels.${app.status}`) || app.status}
                                      </span>
                                    </td>
                                  </tr>

                                  {/* Detail panel */}
                                  {isSelected && (
                                    <tr>
                                      <td colSpan={6} className="p-0">
                                        <div className="bg-dark-bg/50 border-l-2 border-accent p-5 space-y-4">
                                          <div className="flex items-center justify-between">
                                            <h3 className="text-white font-semibold">{app.full_name}</h3>
                                            <button onClick={(e) => { e.stopPropagation(); setSelectedApp(null); }} className="text-gray-400 hover:text-white">
                                              <X className="h-4 w-4" />
                                            </button>
                                          </div>

                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                            <div>
                                              <span className="text-gray-500">{t('dashboard.admin.colEmail')}:</span>
                                              <span className="text-gray-300 ml-2">{app.email}</span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">{t('dashboard.admin.phone')}:</span>
                                              <span className="text-gray-300 ml-2">{app.phone || '—'}</span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">{t('dashboard.admin.colPosition')}:</span>
                                              <span className="text-gray-300 ml-2">{app.position}</span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">{t('dashboard.admin.linkedin')}:</span>
                                              {app.linkedin_url ? (
                                                <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-accent ml-2 hover:underline">{app.linkedin_url}</a>
                                              ) : (
                                                <span className="text-gray-500 ml-2">—</span>
                                              )}
                                            </div>
                                          </div>

                                          {app.cover_letter && (
                                            <div>
                                              <span className="text-gray-500 text-sm">{t('dashboard.admin.coverLetter')}:</span>
                                              <p className="text-gray-300 text-sm mt-1 bg-dark-card rounded-lg p-3 border border-dark-border">{app.cover_letter}</p>
                                            </div>
                                          )}

                                          {/* Status change */}
                                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                            <label className="text-gray-400 text-sm font-medium">{t('dashboard.admin.statusUpdate')}:</label>
                                            <select
                                              value={app.status}
                                              onChange={e => handleStatusChange(app.id, e.target.value)}
                                              className="px-3 py-2 rounded-lg bg-dark-card border border-dark-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                            >
                                              {Object.keys(STATUS_CONFIG).map(s => (
                                                <option key={s} value={s}>{t(`dashboard.statusLabels.${s}`) || s}</option>
                                              ))}
                                            </select>
                                          </div>

                                          {/* Reviewer notes */}
                                          <div>
                                            <label className="text-gray-400 text-sm font-medium block mb-1.5">{t('dashboard.admin.reviewerNotes')}:</label>
                                            <textarea
                                              value={adminApps.find(a => a.id === app.id)?.reviewer_notes || ''}
                                              onChange={e => handleNotesChange(app.id, e.target.value)}
                                              onBlur={() => handleSaveNotes(app.id)}
                                              placeholder={t('dashboard.admin.reviewerNotesPlaceholder')}
                                              rows={3}
                                              className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent transition-all resize-none"
                                            />
                                          </div>

                                          {/* Resume actions */}
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <button
                                              onClick={() => {
                                                if (isDevMode) {
                                                  alert(t('dashboard.admin.resumePlaceholder'));
                                                } else {
                                                  window.open(`${apiClient.baseURL}/api/v1/applications/${app.id}/resume`, '_blank');
                                                }
                                              }}
                                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent/10 border border-accent/20 text-accent text-sm font-medium hover:bg-accent/20 transition-colors"
                                            >
                                              <Eye className="h-4 w-4" />
                                              {t('dashboard.admin.previewResume')}
                                            </button>
                                            <button
                                              onClick={() => {
                                                if (isDevMode) {
                                                  alert(t('dashboard.admin.resumePlaceholder'));
                                                } else {
                                                  const link = document.createElement('a');
                                                  link.href = `${apiClient.baseURL}/api/v1/applications/${app.id}/resume`;
                                                  link.download = '';
                                                  link.click();
                                                }
                                              }}
                                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-card border border-dark-border text-gray-300 text-sm hover:bg-white/5 hover:text-white transition-colors"
                                            >
                                              <Download className="h-4 w-4" />
                                              {t('dashboard.admin.downloadResume')}
                                            </button>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Section 2: Manage Positions ── */}
              <div className="border border-dark-border bg-dark-card rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('positions')}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-accent" />
                    <h2 className="text-white font-semibold text-base">{t('dashboard.admin.positionsTitle')}</h2>
                    <span className="text-xs text-gray-500">({adminPositions.length})</span>
                  </div>
                  {openSections.positions ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                </button>

                {openSections.positions && (
                  <div className="px-5 pb-5 space-y-4">
                    <button
                      onClick={() => setNewPosition({ _isNew: true, title: '', description: '', employment_type: 'full_time' })}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent/10 border border-accent/20 text-accent text-sm font-medium hover:bg-accent/20 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      {t('dashboard.admin.addPosition')}
                    </button>

                    {/* New position form */}
                    {newPosition && (
                      <PositionForm
                        position={newPosition}
                        onSave={handleSavePosition}
                        onCancel={() => setNewPosition(null)}
                        t={t}
                      />
                    )}

                    <div className="space-y-3">
                      {adminPositions.map(pos => (
                        editingPosition === pos.id ? (
                          <PositionForm
                            key={pos.id}
                            position={pos}
                            onSave={handleSavePosition}
                            onCancel={() => setEditingPosition(null)}
                            t={t}
                          />
                        ) : (
                          <div key={pos.id} className="flex items-start justify-between gap-4 p-4 rounded-xl bg-dark-bg/50 border border-dark-border">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-white font-medium text-sm">{pos.title}</h3>
                              <p className="text-gray-400 text-xs mt-1">{pos.description}</p>
                              <span className="inline-block mt-2 text-xs text-gray-500 bg-dark-card px-2 py-0.5 rounded border border-dark-border">
                                {t(`dashboard.admin.employmentTypes.${pos.employment_type}`) || pos.employment_type}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => setEditingPosition(pos.id)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                                title={t('dashboard.admin.editPosition')}
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePosition(pos.id)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                title={t('dashboard.admin.deletePosition')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Section 3: Manage Users ── */}
              <div className="border border-dark-border bg-dark-card rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('users')}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-accent" />
                    <h2 className="text-white font-semibold text-base">{t('dashboard.admin.usersTitle')}</h2>
                    <span className="text-xs text-gray-500">({adminUsers.length})</span>
                  </div>
                  {openSections.users ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                </button>

                {openSections.users && (
                  <div className="px-5 pb-5">
                    {adminUsers.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-8">{t('dashboard.admin.noResults')}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-dark-border text-gray-400 text-left">
                              <th className="pb-3 pr-4 font-medium">{t('dashboard.admin.colUsername')}</th>
                              <th className="pb-3 pr-4 font-medium hidden sm:table-cell">{t('dashboard.admin.colEmail')}</th>
                              <th className="pb-3 pr-4 font-medium">{t('dashboard.admin.colRole')}</th>
                              <th className="pb-3 font-medium hidden md:table-cell">{t('dashboard.admin.colJoined')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminUsers.map(u => (
                              <tr key={u.id} className="border-b border-dark-border/50 hover:bg-white/5 transition-colors">
                                <td className="py-3 pr-4">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center text-xs font-bold text-accent uppercase">
                                      {(u.username || 'U')[0]}
                                    </div>
                                    <span className="text-white font-medium">{u.username}</span>
                                  </div>
                                </td>
                                <td className="py-3 pr-4 text-gray-400 hidden sm:table-cell">{u.email}</td>
                                <td className="py-3 pr-4">
                                  <select
                                    value={u.role}
                                    onChange={e => handleRoleChange(u.id, e.target.value)}
                                    className="px-2 py-1 rounded-md bg-dark-bg border border-dark-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-accent"
                                  >
                                    <option value="employee">{t('dashboard.admin.roles.employee')}</option>
                                    <option value="manager">{t('dashboard.admin.roles.manager')}</option>
                                    <option value="admin">{t('dashboard.admin.roles.admin')}</option>
                                  </select>
                                </td>
                                <td className="py-3 text-gray-400 hidden md:table-cell">{formatDate(u.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </section>

      {/* Spacer */}
      <div className="h-20" />
    </div>
  );
};

export default Dashboard;
